<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CaptainUsageAPI from 'dashboard/api/captain/usage';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PurchaseCreditsModal from 'dashboard/routes/dashboard/settings/billing/components/PurchaseCreditsModal.vue';

const { t } = useI18n();

const isFetching = ref(false);
const range = ref('30');
const summary = ref(null);
const byFeature = ref([]);
const byModel = ref([]);
const byInbox = ref([]);
const executionLimit = ref(null);
const purchaseModalRef = ref(null);

const rangeOptions = [
  { value: '7', label: t('CAPTAIN.USAGE.RANGE.LAST_7') },
  { value: '30', label: t('CAPTAIN.USAGE.RANGE.LAST_30') },
  { value: '90', label: t('CAPTAIN.USAGE.RANGE.LAST_90') },
];

const formatNumber = value => new Intl.NumberFormat().format(value || 0);

const formatCurrency = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value || 0);

const fetchUsage = async () => {
  isFetching.value = true;
  try {
    const from = new Date();
    from.setDate(from.getDate() - Number(range.value));
    const { data } = await CaptainUsageAPI.get({
      from: from.toISOString().slice(0, 10),
    });
    summary.value = data.summary;
    byFeature.value = data.by_feature || [];
    byModel.value = data.by_model || [];
    byInbox.value = data.by_inbox || [];
    executionLimit.value = data.limits?.executions || null;
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.USAGE.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const handleRangeChange = () => fetchUsage();

const openPurchaseModal = () => purchaseModalRef.value?.open();

const handlePurchaseSuccess = () => fetchUsage();

onMounted(() => fetchUsage());
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.USAGE.TITLE')"
    :show-pagination-footer="false"
    :show-know-more="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <div class="flex flex-col gap-6 max-w-5xl mx-auto w-full pb-8">
        <div class="flex items-center justify-between gap-4">
          <p class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.USAGE.DESCRIPTION') }}
          </p>
          <select
            v-model="range"
            class="px-3 py-2 text-sm rounded-lg bg-n-alpha-black2 border border-n-weak text-n-slate-12"
            @change="handleRangeChange"
          >
            <option
              v-for="option in rangeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div v-if="isFetching" class="flex items-center justify-center py-16">
          <Spinner />
        </div>

        <template v-else>
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2">
              <span class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.USAGE.CARDS.EXECUTIONS') }}
              </span>
              <span class="text-2xl font-medium text-n-slate-12">
                {{ formatNumber(summary?.executions) }}
              </span>
            </div>
            <div class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2">
              <span class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.USAGE.CARDS.SUCCESS') }}
              </span>
              <span class="text-2xl font-medium text-n-teal-11">
                {{ formatNumber(summary?.success) }}
              </span>
            </div>
            <div class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2">
              <span class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.USAGE.CARDS.ERRORS') }}
              </span>
              <span class="text-2xl font-medium text-n-ruby-11">
                {{ formatNumber(summary?.errors) }}
              </span>
            </div>
            <div class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2">
              <span class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.USAGE.CARDS.TOTAL_TOKENS') }}
              </span>
              <span class="text-2xl font-medium text-n-slate-12">
                {{ formatNumber(summary?.total_tokens) }}
              </span>
            </div>
            <div class="flex flex-col gap-1 p-4 rounded-xl bg-n-solid-2">
              <span class="text-sm text-n-slate-11">
                {{ t('CAPTAIN.USAGE.CARDS.ESTIMATED_COST') }}
              </span>
              <span class="text-2xl font-medium text-n-slate-12">
                {{ formatCurrency(summary?.estimated_cost) }}
              </span>
            </div>
          </div>

          <div
            v-if="executionLimit && executionLimit.total_count < 1000000000"
            class="flex flex-col gap-2 p-4 rounded-xl bg-n-solid-2"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CAPTAIN.USAGE.QUOTA.TITLE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ formatNumber(executionLimit.consumed) }} /
                {{ formatNumber(executionLimit.total_count) }}
              </span>
            </div>
            <div class="h-2 rounded-full bg-n-alpha-black2 overflow-hidden">
              <div
                class="h-full rounded-full bg-n-teal-9"
                :style="{
                  width: `${Math.min(
                    100,
                    (executionLimit.consumed / executionLimit.total_count) * 100
                  )}%`,
                }"
              />
            </div>
            <span class="text-xs text-n-slate-11">
              {{
                t('CAPTAIN.USAGE.QUOTA.REMAINING', {
                  count: formatNumber(executionLimit.current_available),
                })
              }}
            </span>
            <span
              v-if="executionLimit.overage > 0"
              class="text-xs font-medium text-n-ruby-11"
            >
              {{
                t('CAPTAIN.USAGE.QUOTA.OVERAGE', {
                  count: formatNumber(executionLimit.overage),
                })
              }}
            </span>
            <button
              v-if="executionLimit.overage > 0"
              type="button"
              class="self-start mt-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10"
              @click="openPurchaseModal"
            >
              {{ t('CAPTAIN.USAGE.QUOTA.BUY_EXECUTIONS') }}
            </button>
          </div>

          <div class="flex flex-col gap-3">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('CAPTAIN.USAGE.BY_FEATURE') }}
            </h3>
            <div class="rounded-xl border border-n-weak overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-solid-2 text-n-slate-11">
                  <tr>
                    <th class="text-left font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.FEATURE') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.EXECUTIONS') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.TOKENS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in byFeature"
                    :key="row.name"
                    class="border-t border-n-weak text-n-slate-12"
                  >
                    <td class="px-4 py-2">{{ row.name || '—' }}</td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.executions) }}
                    </td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.total_tokens) }}
                    </td>
                  </tr>
                  <tr v-if="!byFeature.length">
                    <td class="px-4 py-6 text-center text-n-slate-11" colspan="3">
                      {{ t('CAPTAIN.USAGE.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('CAPTAIN.USAGE.BY_MODEL') }}
            </h3>
            <div class="rounded-xl border border-n-weak overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-solid-2 text-n-slate-11">
                  <tr>
                    <th class="text-left font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.MODEL') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.EXECUTIONS') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.TOKENS') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.COST') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in byModel"
                    :key="row.name"
                    class="border-t border-n-weak text-n-slate-12"
                  >
                    <td class="px-4 py-2">{{ row.name || '—' }}</td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.executions) }}
                    </td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.total_tokens) }}
                    </td>
                    <td class="px-4 py-2 text-right">
                      {{ formatCurrency(row.estimated_cost) }}
                    </td>
                  </tr>
                  <tr v-if="!byModel.length">
                    <td class="px-4 py-6 text-center text-n-slate-11" colspan="4">
                      {{ t('CAPTAIN.USAGE.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="flex flex-col gap-3">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t('CAPTAIN.USAGE.BY_INBOX') }}
            </h3>
            <div class="rounded-xl border border-n-weak overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-n-solid-2 text-n-slate-11">
                  <tr>
                    <th class="text-left font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.INBOX') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.EXECUTIONS') }}
                    </th>
                    <th class="text-right font-medium px-4 py-2">
                      {{ t('CAPTAIN.USAGE.TABLE.TOKENS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in byInbox"
                    :key="row.name"
                    class="border-t border-n-weak text-n-slate-12"
                  >
                    <td class="px-4 py-2">{{ row.name || '—' }}</td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.executions) }}
                    </td>
                    <td class="px-4 py-2 text-right">
                      {{ formatNumber(row.total_tokens) }}
                    </td>
                  </tr>
                  <tr v-if="!byInbox.length">
                    <td class="px-4 py-6 text-center text-n-slate-11" colspan="3">
                      {{ t('CAPTAIN.USAGE.EMPTY') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </template>
    <PurchaseCreditsModal
      ref="purchaseModalRef"
      kind="executions"
      @success="handlePurchaseSuccess"
    />
  </PageLayout>
</template>
